import type { FC, ElementType } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Collapse, List, Typography, Divider } from '@material-ui/core';
import { ChevronRight as ChevronRightIcon } from '../icons/chevron-right';
import { ChevronDown as ChevronDownIcon } from '../icons/chevron-down';
import { ExternalLink as ExternalLinkIcon } from '../icons/external-link';
import { IpopLetter } from './ipop-letter';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { confidenceIndice as confidenceIndiceMap } from '../static-data';
import { ConfidenceSegments } from './general/confidence-segments';

interface DashboardSidebarItemProps {
  active: boolean;
  activeHref: string;
  external?: boolean;
  href?: string;
  icon?: IconProp;
  items?: Array<{ href: string; title: string; confidenceIndice: number; isChild?: boolean }>
  onOpen: () => void;
  open: boolean;
  pinned: boolean;
  title: string;
  confidenceIndice?: number;
  /** Colored IPOP letter chip (U/A/S/F) shown instead of the confidence dot. */
  ipopLetter?: string;
  /** 0-100 section completion, shown as a thin progress bar. */
  completion?: number;
  /** Renders a small uppercase group heading instead of a nav item. */
  isGroupLabel?: boolean;
  /** Count badge shown at the end of the item (e.g. open unknowns). */
  badge?: { count: number; color?: 'error' | 'warning' | 'primary' | 'info' | 'success' };
}

export const DashboardSidebarItem: FC<DashboardSidebarItemProps> = (props) => {
  const {
    active,
    activeHref,
    external,
    href,
    icon: Icon,
    items,
    onOpen,
    open,
    pinned,
    title,
    confidenceIndice,
    ipopLetter,
    completion,
    isGroupLabel,
    badge
  } = props;
  const { t } = useTranslation();

  // Group heading
  if (isGroupLabel) {
    return pinned ? (
      <Typography
        sx={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontWeight: 700,
          color: 'text.disabled',
          px: 1.25,
          pt: 2,
          pb: 0.5,
        }}
      >
        {title}
      </Typography>
    ) : (
      <Box sx={{ height: 14 }} />
    );
  }

  // Colored IPOP chip takes precedence over the confidence dot.
  const startIconNode = ipopLetter ? (
    <IpopLetter letter={ipopLetter} size={18} />
  ) : Icon ? (
    <FontAwesomeIcon icon={Icon} />
  ) : (
    <ConfidenceSegments level={confidenceIndice || 0} compact />
  );

  // Color the completion-bar fill by the review's lowest confidence index (the
  // `confidenceIndice` prop is already that minimum), reusing the same palette as
  // the confidence dot instead of a fixed blue. Falls back to a neutral fill when
  // no confidence is available so the bar never renders a broken color; stays
  // white on the active (highlighted) item in that case so it remains visible.
  const completionColor =
    confidenceIndice && confidenceIndiceMap[confidenceIndice]
      ? confidenceIndiceMap[confidenceIndice]['color']
      : active
      ? '#fff'
      : 'neutral.300';

  // Thin section-completion bar (shown only when expanded).
  const completionBar =
    completion != null ? (
      <Box
        sx={{
          display: pinned ? 'block' : 'none',
          width: 40,
          height: 4,
          borderRadius: 999,
          bgcolor: active ? 'rgba(255,255,255,0.3)' : 'neutral.200',
          ml: 1,
          mr: 0.5,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.max(0, Math.min(100, completion))}%`,
            bgcolor: completionColor,
            borderRadius: 999,
          }}
        />
      </Box>
    ) : null;

  // Count badge (e.g. open unknowns), shown when expanded.
  const badgeNode =
    badge && badge.count > 0 ? (
      <Chip
        label={badge.count}
        size="small"
        color={badge.color || 'error'}
        sx={{
          display: pinned ? 'inline-flex' : 'none',
          height: 18,
          ml: 0.5,
          fontSize: 10,
          fontWeight: 700,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    ) : null;

  // Branch
  if (items) {
    return (
      <List
        disablePadding
        sx={{ width: '100%' }}
      >
        <li data-tour={title}>
          <Button
            endIcon={
              open
                ? <ChevronDownIcon fontSize="small" />
                : <ChevronRightIcon fontSize="small" />
            }
            fullWidth
            onClick={onOpen}
            startIcon={startIconNode}
            sx={{
              justifyContent: 'flex-start',
              lineHeight: 0,
              minWidth: 'fit-content',
              px: Icon ? 1.25 : 0,
              // py: 1.25,
              my: 0.5,
              '& .MuiButton-startIcon': {
                color: active ? 'primary' : 'neutral.400',
                margin: 0
              },
              '& .MuiButton-endIcon': {
                color: active ? 'primary' : 'neutral.400',
                display: pinned ? 'flex' : 'none',
                marginLeft: 'auto',
              },
              backgroundColor: active ? 'primary.main' : 'transparent',
              color: active ? 'primary.contrastText' : 'neutral.400',
              '&:hover': {
                backgroundColor: active ? 'primary.dark' : ''
              }
            }}
            variant="text"
          >
            <Typography
              color="textPrimary"
              sx={{
                color: active ? 'primary' : 'neutral.400',
                display: pinned ? 'flex' : 'none',
                ml: 1.25,
                flex: 1,
                // fontSize: 12
              }}
              variant="inherit"
            >
              {t(title)}
            </Typography>
            {completionBar}
            {badgeNode}
          </Button>
        </li>
        <Collapse
          in={open}
          unmountOnExit
        >
          <List
            disablePadding
            sx={{ width: '100%' }}
          >
            {items.map((item) => {
              const isActive = activeHref === item.href;

              return (
                <>
                  <li
                    key={item.href}
                    style={{
                      textOverflow: 'ellipsis',
                      // Indent individual customers/investors under their section
                      // (Customers / Investors & Companies) with a vertical
                      // connector line so the two-level hierarchy reads at a
                      // glance; section rows (Customers, Solution, company_fit)
                      // stay flush-left. Gated on `pinned` so the collapsed rail
                      // is unaffected.
                      paddingLeft: pinned && item.isChild ? 20 : 0,
                      marginLeft: pinned && item.isChild ? 14 : 0,
                      borderLeft: pinned && item.isChild ? '2px solid rgba(0,0,0,0.12)' : 'none',
                    }}
                  >
                    <Button
                      component={RouterLink}
                      fullWidth
                      // startIcon={
                      //   <FontAwesomeIcon icon={faCircle} size="xs" style={{width: 12}} color={item.confidenceIndice ? confidenceIndiceMap[item.confidenceIndice]['color'] : 'transparent'} />
                      // }
                      startIcon={<ConfidenceSegments level={item.confidenceIndice || 0} compact />}
                      sx={{
                        color: isActive ? 'neutral.900' : 'neutral.400',
                        fontWeight: isActive ? 600 : 400,
                        // fontWeight: 400,
                        justifyContent: 'flex-start',
                        pl: 0,
                        ml: 0,
                        fontSize: 8,
                      }}
                      to={item.href}
                    // variant="text"
                    >
                      <Typography
                        color="textPrimary"
                        // Section rows (Customers, Solution, company_fit) stay
                        // body1; only the indented children shrink to body2.
                        // Keyed on `isChild`, not on the presence of a
                        // confidence index — Solution is a section row *and*
                        // carries one.
                        variant={item.isChild ? "body2" : "body1"}
                        sx={{ fontWeight: isActive ? 600 : 400 }}
                      >
                        {t(item.title)}
                      </Typography>
                    </Button>
                  </li>
                  <Divider />
                </>
              );
            })}
          </List>
        </Collapse>
      </List>
    );
  }

  // Leaf
  return (
    <li data-tour={title}>
      <Button
        component={RouterLink}
        endIcon={external && <ExternalLinkIcon sx={{ color: 'action.disabled' }} />}
        fullWidth
        startIcon={startIconNode}
        target={external ? '_target' : '_self'}
        sx={{
          justifyContent: 'flex-start',
          lineHeight: 0,
          minWidth: 'fit-content',
          px: 1.25,
          // py: 1.25,
          my: 0.5,
          '& .MuiButton-startIcon': {
            color: active ? 'primary' : 'neutral.400',
            margin: 0
          },
          '& .MuiButton-endIcon': {
            color: active ? 'primary' : 'neutral.400',
            display: pinned ? 'flex' : 'none',
            marginLeft: 'auto'
          },
          backgroundColor: active ? 'primary.main' : 'transparent',
          color: active ? 'primary.contrastText' : 'primary.main',
          '&:hover': {
            backgroundColor: active ? 'primary.dark' : ''
          }
        }}
        to={href}
        variant="text"
      >
        <Typography
          color="textPrimary"
          sx={{
            color: active ? 'primary' : 'neutral.400',
            display: pinned ? 'flex' : 'none',
            ml: 1.25,
            flex: 1,
            // fontSize: 12
          }}
          variant="inherit"
        >
          {t(title)}
        </Typography>
        {completionBar}
        {badgeNode}
      </Button>
    </li>
  );
};

DashboardSidebarItem.defaultProps = {
  open: false,
  pinned: false
};

DashboardSidebarItem.propTypes = {
  active: PropTypes.bool,
  activeHref: PropTypes.string,
  external: PropTypes.bool,
  href: PropTypes.string,
  // @ts-ignore
  icon: PropTypes.any,
  items: PropTypes.array,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  pinned: PropTypes.bool,
  title: PropTypes.string
};
